import { useEffect, useRef, useState } from "react";
import { Archive, MessageSquare, Paperclip, Send, Trash2 } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { apiFetch, ensureCsrf } from "@contractor-quoting/api-client";
import { Badge, Button, Card } from "@contractor-quoting/shared-ui";
import { clearChatDraft, loadChatDraft, saveChatDraft } from "../chat-draft.js";
import { ChatMessageContent } from "../components/ChatMessageContent.js";
import { CustomerWorkspaceShell } from "../components/CustomerWorkspaceShell.js";
import { ProjectActionCards } from "../components/ProjectActionCards.js";
import { ProjectContextPanel } from "../components/ProjectContextPanel.js";
import { usePendingChatReconciliation } from "../hooks/usePendingChatReconciliation.js";
import type { QuoteFormModel } from "../structured-intake-model.js";

interface ProjectListItem {
  id: string;
  title: string | null;
  status: string;
  can_delete?: boolean;
  project_type: string;
  job_type_name?: string;
  job_status?: string;
  payment_status?: string;
  updated_at: string;
  contractor?: { name: string; slug: string };
}
interface TranscriptMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  createdAt: string;
  state: "queued" | "running" | "completed" | "failed" | "cancelled";
  error: string | null;
}
export interface ProjectDetail {
  id: string;
  title: string | null;
  status: string;
  job_type_name: string | null;
  job_status: string;
  payment_status: string;
  tradeScopes: Array<{ trade_code: string; coverage: "covered" | "uncovered" }>;
  serviceLocation: { city: string; region: string; country: string } | null;
  summary: null | {
    approximateLayoutSvg: string;
    quantities: { requiredMaterial: { squareFeet: string } };
  };
  estimates: Array<{
    total_low_minor: string;
    total_high_minor: string;
    currency: string;
    version: number;
    price_presentation?: "single" | "range" | "starting_at";
    generated_total_minor?: string | null;
  }>;
  priceActivity?: Array<{
    sequence: number;
    event_type: string;
    customer_message: string;
    evidence_refs: string[];
    created_at: string;
  }>;
  submission: { submitted_at: string } | null;
  images: Array<{ id: string; filename: string; contentType: string; readUrl: string }>;
  walkthroughs: Array<{
    id: string;
    status: string;
    slots: Array<{ id: string; startsAt: string; endsAt: string; status: string }>;
  }>;
  quotes: Array<{
    id: string;
    version_id: string;
    quote_number: string;
    status: string;
    total_minor: string;
    currency: string;
    readUrl: string | null;
    scope: string;
    terms: string;
  }>;
  invoices: Array<{
    id: string;
    invoice_number: string;
    status: string;
    total_minor: string;
    currency: string;
    due_date: string;
    readUrl: string | null;
    receiptReadUrl: string | null;
  }>;
  intakeReview: null | {
    id: string;
    state_version: number;
    status: "prepared" | "confirmed";
    summary: {
      scopes: Array<{ id: string; type: string; label: string }>;
      facts: Array<{ id: string; field: string; value: unknown }>;
      measurements: Array<{ id: string; type: string; value: number; unit: string }>;
      exclusions: Array<{ requirement_id: string; accuracy_impact: string }>;
    };
    accuracy_gaps: Array<{ id: string; message: string; impact: string }>;
  };
  intakeState?: {
    workItems: Array<{ id: string; canonical_type: string; label: string }>;
    facts: Array<{ id: string; field: string; normalized_value: unknown; raw_value: unknown }>;
    measurements: Array<{
      id: string;
      measurement_type: string;
      raw_value: string;
      raw_unit: string;
    }>;
    accuracyGaps: Array<{ id: string; message: string }>;
    quoteForm?: QuoteFormModel;
  };
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const archived = searchParams.get("archived") === "true";
  const [message, setMessage] = useState("Opening your latest project…");
  const [archivedItems, setArchivedItems] = useState<ProjectListItem[]>([]);
  useEffect(() => {
    void apiFetch<{ items: ProjectListItem[] }>(
      `/v1/customer/projects${archived ? "?archived=true" : ""}`,
    )
      .then(({ items }) => {
        if (archived) {
          setArchivedItems(items);
          setMessage(items.length ? "" : "No archived projects.");
          return;
        }
        if (items[0]) void navigate(`/projects/${items[0].id}`, { replace: true });
        else setMessage("Start a project by choosing a contractor.");
      })
      .catch((error: unknown) =>
        setMessage(error instanceof Error ? error.message : "Projects unavailable"),
      );
  }, [archived, navigate]);
  if (archived) {
    return (
      <CustomerWorkspaceShell>
        <main className="mx-auto w-full max-w-3xl p-5 sm:p-8">
          <h1 className="text-2xl font-black">Archived projects</h1>
          <p className="mt-2 text-stone-500">
            Hidden from your active chats. Restoring does not change contractor workflow status.
          </p>
          <div className="mt-6 space-y-3">
            {archivedItems.map((project) => (
              <Card key={project.id} className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="truncate font-bold">{project.title ?? "Untitled project"}</p>
                  <p className="text-sm capitalize text-stone-500">
                    {project.status.replaceAll("_", " ")}
                  </p>
                </div>
                <Button
                  onClick={() =>
                    void ensureCsrf()
                      .then(() =>
                        apiFetch(`/v1/customer/projects/${project.id}/restore`, {
                          method: "POST",
                        }),
                      )
                      .then(() =>
                        setArchivedItems((current) =>
                          current.filter((item) => item.id !== project.id),
                        ),
                      )
                  }
                >
                  Restore
                </Button>
              </Card>
            ))}
            {message ? <p className="py-10 text-center text-stone-500">{message}</p> : null}
          </div>
          <Link className="mt-6 inline-block font-bold text-emerald-800 underline" to="/projects">
            Back to active projects
          </Link>
        </main>
      </CustomerWorkspaceShell>
    );
  }
  return (
    <CustomerWorkspaceShell>
      <div className="grid h-full place-items-center p-8 text-center">
        <div>
          <MessageSquare className="mx-auto text-emerald-700" size={38} />
          <h1 className="mt-4 text-2xl font-black">Your project workspace</h1>
          <p className="mt-2 text-stone-500">{message}</p>
          <Button className="mt-6" onClick={() => void navigate("/projects/contractors")}>
            Find a contractor
          </Button>
        </div>
      </div>
    </CustomerWorkspaceShell>
  );
}

export function ProjectDetailPage() {
  const { projectId = "" } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [retryMessageId, setRetryMessageId] = useState<string | null>(
    () => loadChatDraft(projectId)?.clientMessageId ?? null,
  );
  const [draft, setDraft] = useState(() => loadChatDraft(projectId)?.input ?? "");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const load = async () => {
    const [detail, history, intakeState] = await Promise.all([
      apiFetch<ProjectDetail>(`/v1/customer/projects/${projectId}`),
      apiFetch<{ sessionId: string | null; messages: TranscriptMessage[]; pending: boolean }>(
        `/v1/customer/projects/${projectId}/chat`,
      ),
      apiFetch<NonNullable<ProjectDetail["intakeState"]>>(
        `/v1/customer/projects/${projectId}/intake`,
      ),
    ]);
    setProject({ ...detail, intakeState });
    setMessages(history.messages);
    setSessionId(history.sessionId);
    setPending(history.pending);
    setHydrated(true);
  };
  useEffect(() => {
    setHydrated(false);
    const savedDraft = loadChatDraft(projectId);
    setDraft(savedDraft?.input ?? "");
    setRetryMessageId(savedDraft?.clientMessageId ?? null);
    void ensureCsrf()
      .then(load)
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : "Project unavailable");
        setHydrated(true);
      });
  }, [projectId]);
  usePendingChatReconciliation<{
    sessionId: string | null;
    messages: TranscriptMessage[];
    pending: boolean;
  }>({
    enabled: pending,
    endpoint: `/v1/customer/projects/${projectId}/chat/reconcile`,
    onResult: (chat) => {
      setSessionId(chat.sessionId);
      setMessages(chat.messages);
      setPending(chat.pending);
      void load();
    },
    onAuthenticationError: (reason) => {
      setPending(false);
      setError(reason.message);
    },
  });
  const changeDraft = (value: string, clientMessageId?: string) => {
    setDraft(value);
    setRetryMessageId(clientMessageId ?? null);
    if (value) saveChatDraft(projectId, [], value, clientMessageId);
    else clearChatDraft(projectId);
  };
  const send = async (retryText?: string) => {
    const text = (retryText ?? draft).trim();
    if (!text || sending) return;
    setSending(true);
    setError("");
    const clientMessageId = retryText
      ? crypto.randomUUID()
      : (retryMessageId ?? crypto.randomUUID());
    try {
      if (!sessionId) {
        await apiFetch(`/v1/customer/projects/${projectId}/chat/session`, { method: "POST" });
      }
      setMessages((current) => [
        ...current,
        {
          id: clientMessageId,
          role: "user",
          text,
          state: "queued",
          error: null,
          createdAt: new Date().toISOString(),
        },
      ]);
      changeDraft("");
      const response = await apiFetch<{
        sessionId: string | null;
        messages: TranscriptMessage[];
        pending: boolean;
      }>(`/v1/customer/projects/${projectId}/chat/messages`, {
        method: "POST",
        body: JSON.stringify({ message: text, clientMessageId }),
      });
      setSessionId(response.sessionId);
      setMessages(response.messages);
      setPending(response.pending);
      setRetryMessageId(null);
      if (!response.pending) await load();
    } catch (reason) {
      changeDraft(text, clientMessageId);
      setError(
        reason instanceof Error
          ? `${reason.message} Your message is saved—try again.`
          : "Message failed. Your draft is saved.",
      );
    } finally {
      setSending(false);
    }
  };
  const upload = async (file: File) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Choose a JPEG, PNG, or WebP image.");
      return;
    }
    const created = await apiFetch<{
      imageId: string;
      upload: { url: string; fields: Record<string, string> };
    }>(`/v1/customer/projects/${projectId}/images/upload-policy`, {
      method: "POST",
      body: JSON.stringify({ filename: file.name, contentType: file.type, sizeBytes: file.size }),
    });
    const form = new FormData();
    Object.entries(created.upload.fields).forEach(([key, value]) => form.append(key, value));
    form.append("file", file);
    const uploaded = await fetch(created.upload.url, { method: "POST", body: form });
    if (!uploaded.ok) throw new Error("Image upload failed");
    await apiFetch(`/v1/customer/projects/${projectId}/images/${created.imageId}/complete`, {
      method: "POST",
    });
    await load();
  };
  const latest = project?.estimates[0];
  const removeOrArchive = async () => {
    if (!project) return;
    const title = project.title ?? "Untitled project";
    const deletable =
      ["exploring", "draft", "ready_for_estimate", "estimated"].includes(project.status) &&
      !project.submission &&
      project.quotes.length === 0 &&
      project.invoices.length === 0;
    if (deletable) {
      const confirmationTitle = window.prompt(
        `Delete “${title}”? Its chat and uploaded draft material will be permanently removed.\n\nType the project name to continue:`,
      );
      if (confirmationTitle !== title) return;
      await apiFetch(`/v1/customer/projects/${projectId}`, {
        method: "DELETE",
        body: JSON.stringify({ confirmationTitle }),
      });
    } else {
      if (!window.confirm(`Archive “${title}”? You can restore it from Archived projects.`)) return;
      await apiFetch(`/v1/customer/projects/${projectId}/archive`, { method: "POST" });
    }
    void navigate("/projects");
  };
  return (
    <CustomerWorkspaceShell activeProjectId={projectId}>
      <div className="grid h-full min-h-0 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section className="flex min-h-0 flex-col bg-white">
          <header className="flex min-h-16 items-center justify-between gap-3 border-b border-stone-200 px-5 py-2">
            <div>
              <h1 className="font-bold">{project?.title ?? "Project chat"}</h1>
              <p className="text-xs text-stone-500">
                {project?.serviceLocation
                  ? `${project.serviceLocation.city}, ${project.serviceLocation.region}`
                  : "Private project"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {project ? (
                <Badge>{(project.job_status ?? project.status).replaceAll("_", " ")}</Badge>
              ) : null}
              {project ? (
                <button
                  className="rounded-lg border border-stone-300 p-2 text-stone-600 hover:bg-stone-100"
                  onClick={() =>
                    void removeOrArchive().catch((reason: unknown) =>
                      setError(reason instanceof Error ? reason.message : "Project action failed"),
                    )
                  }
                  title={project.submission ? "Archive project" : "Delete or archive project"}
                  type="button"
                >
                  {project.submission ? <Archive size={17} /> : <Trash2 size={17} />}
                </button>
              ) : null}
            </div>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-8 sm:px-8">
            <div className="mx-auto max-w-3xl space-y-5">
              {!hydrated ? (
                <div className="py-16 text-center text-stone-500">
                  Restoring your project conversation…
                </div>
              ) : null}
              {hydrated && messages.length === 0 ? (
                <div className="py-16 text-center">
                  <MessageSquare className="mx-auto text-emerald-700" />
                  <h2 className="mt-4 text-2xl font-black">Let’s plan your project</h2>
                  <p className="mt-2 text-stone-500">
                    Describe what you’re changing, or add reference photos.
                  </p>
                </div>
              ) : null}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[82%] rounded-2xl px-4 py-3 leading-relaxed ${message.role === "user" ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-800"}`}
                  >
                    <ChatMessageContent
                      markdown={message.role === "assistant"}
                      text={message.text}
                    />
                    {message.role === "user" && ["queued", "running"].includes(message.state) ? (
                      <div className="mt-1 text-xs text-emerald-100">
                        {message.state === "queued" ? "Starting project guide…" : "Working…"}
                      </div>
                    ) : null}
                    {message.role === "user" && message.state === "failed" ? (
                      <button
                        className="mt-2 block text-xs font-bold text-emerald-100 underline"
                        onClick={() => void send(message.text)}
                        type="button"
                      >
                        Retry
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
              {project ? (
                <ProjectActionCards project={project} latest={latest} reload={load} />
              ) : null}
              {pending ? (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-stone-100 px-4 py-3 text-stone-500">
                    Project guide is working…
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div className="border-t border-stone-200 bg-white p-4">
            <div className="mx-auto max-w-3xl">
              <div className="rounded-2xl border border-stone-300 bg-white p-3 shadow-sm focus-within:border-emerald-600">
                <textarea
                  aria-label="Project message"
                  rows={2}
                  className="w-full resize-none border-0 bg-transparent p-1 outline-none"
                  placeholder="Message your project guide…"
                  value={draft}
                  onChange={(event) => changeDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void send();
                    }
                  }}
                />
                <div className="mt-2 flex items-center justify-between">
                  <button
                    aria-label="Upload reference image"
                    className="rounded-lg p-2 text-stone-500 hover:bg-stone-100"
                    onClick={() => fileRef.current?.click()}
                  >
                    <Paperclip size={20} />
                  </button>
                  <input
                    ref={fileRef}
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file)
                        void upload(file).catch((reason: unknown) =>
                          setError(reason instanceof Error ? reason.message : "Upload failed"),
                        );
                    }}
                  />
                  <Button disabled={!draft.trim() || sending} onClick={() => void send()}>
                    {sending ? (
                      "Sending…"
                    ) : (
                      <>
                        <Send size={17} /> Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {error ? <p className="mt-2 text-sm font-semibold text-red-700">{error}</p> : null}
              <p className="mt-2 text-center text-xs text-stone-400">
                Your guide can use project answers, never private image bytes or your address.
              </p>
            </div>
          </div>
        </section>
        <aside className="hidden min-h-0 overflow-y-auto border-l border-stone-200 bg-stone-50 p-4 xl:block">
          <h2 className="font-black">Project context</h2>
          <ProjectContextPanel project={project} reload={load} />
        </aside>
      </div>
    </CustomerWorkspaceShell>
  );
}
